import { alpha } from "@mui/material/styles";

export const dashPageBgSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    position: "relative",
    minHeight: "100vh",
    background: isDark
      ? `radial-gradient(1200px 500px at 12% -10%, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 55%),
         radial-gradient(900px 420px at 100% 0%, ${alpha("#00bcd4", 0.14)} 0%, transparent 50%),
         ${theme.palette.background.default}`
      : `radial-gradient(1200px 520px at 8% -8%, ${alpha("#00bcd4", 0.14)} 0%, transparent 55%),
         radial-gradient(900px 400px at 100% 0%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 48%),
         linear-gradient(180deg, ${alpha("#f7fafb", 1)} 0%, ${theme.palette.background.default} 42%)`,
  };
};

export const dashHeroSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    position: "sticky",
    top: 0,
    zIndex: 20,
    mb: 2,
    borderRadius: 3.5,
    overflow: "hidden",
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, isDark ? 0.9 : 0.7),
    background: isDark
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.32)} 0%, ${alpha("#00bcd4", 0.14)} 42%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
      : `linear-gradient(135deg, ${alpha("#dff5f7", 0.95)} 0%, ${alpha("#eef2ff", 0.7)} 45%, ${alpha("#ffffff", 0.92)} 100%)`,
    backdropFilter: "blur(14px)",
    boxShadow: isDark
      ? `0 12px 40px ${alpha("#000", 0.45)}`
      : `0 14px 40px ${alpha("#0f172a", 0.07)}`,
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: isDark
        ? `radial-gradient(600px 180px at 85% 0%, ${alpha("#00bcd4", 0.22)}, transparent 70%)`
        : `radial-gradient(640px 200px at 90% -10%, ${alpha("#00bcd4", 0.22)}, transparent 70%)`,
      pointerEvents: "none",
    },
    "&::after": {
      content: '""',
      display: "block",
      height: 3,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha("#00bcd4", 0.9)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.light, 0.7)})`,
    },
  };
};

export const dashToolbarBtnSx = (theme, { primary = false } = {}) => {
  const isDark = theme.palette.mode === "dark";
  return {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    px: 1.5,
    ...(primary
      ? {
          boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.32)}`,
          "&:hover": {
            boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.4)}`,
          },
        }
      : {
          borderColor: alpha(theme.palette.divider, isDark ? 1 : 0.9),
          color: theme.palette.text.primary,
          backgroundColor: alpha(
            theme.palette.background.paper,
            isDark ? 0.7 : 0.55,
          ),
          backdropFilter: "blur(8px)",
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.06),
          },
        }),
  };
};

export const dashTileSx = (theme, { editing = false } = {}) => {
  const isDark = theme.palette.mode === "dark";
  return {
    height: "100%",
    position: "relative",
    overflow: "hidden",
    borderRadius: 4,
    border: "1px solid",
    borderColor: editing
      ? alpha(theme.palette.primary.main, 0.45)
      : isDark
        ? alpha(theme.palette.common.white, 0.1)
        : alpha("#fff", 0.7),
    background: isDark
      ? `linear-gradient(155deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.16)} 100%)`
      : `linear-gradient(155deg, ${alpha("#ffffff", 0.88)} 0%, ${alpha("#f0fafb", 0.72)} 55%, ${alpha("#e8f6f8", 0.55)} 100%)`,
    backdropFilter: "blur(18px) saturate(1.35)",
    WebkitBackdropFilter: "blur(18px) saturate(1.35)",
    boxShadow: editing
      ? isDark
        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.28)}, 0 18px 40px ${alpha("#000", 0.4)}, inset 0 1px 0 ${alpha("#fff", 0.08)}`
        : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}, 0 18px 40px ${alpha("#0f172a", 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.65)}`
      : isDark
        ? `0 12px 32px ${alpha("#000", 0.38)}, inset 0 1px 0 ${alpha("#fff", 0.07)}`
        : `0 2px 4px ${alpha("#0f172a", 0.02)},
           0 12px 28px ${alpha("#0f172a", 0.05)},
           0 28px 48px ${alpha("#00bcd4", 0.04)},
           inset 0 1px 0 ${alpha("#fff", 0.9)}`,
    transition:
      "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s ease, border-color 0.28s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "8%",
      right: "8%",
      height: 1,
      background: `linear-gradient(90deg, transparent, ${alpha("#fff", isDark ? 0.18 : 0.95)}, transparent)`,
      pointerEvents: "none",
      zIndex: 1,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -40,
      right: -30,
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha("#00bcd4", isDark ? 0.2 : 0.14)} 0%, transparent 70%)`,
      pointerEvents: "none",
      zIndex: 0,
      transition: "opacity 0.28s ease, transform 0.28s ease",
      opacity: editing ? 0.9 : 0.55,
    },
    "&:hover": editing
      ? undefined
      : {
          transform: "translateY(-4px) scale(1.01)",
          borderColor: alpha(theme.palette.primary.main, isDark ? 0.4 : 0.28),
          boxShadow: isDark
            ? `0 18px 44px ${alpha("#000", 0.48)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`
            : `0 8px 16px ${alpha("#0f172a", 0.04)},
               0 22px 48px ${alpha("#0f172a", 0.08)},
               0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)},
               inset 0 1px 0 ${alpha("#fff", 1)}`,
          "&::after": {
            opacity: 1,
            transform: "scale(1.15)",
          },
        },
  };
};

/** Paleta por sección del catálogo (atajos / widgets). */
export const DASH_SECTION_ACCENTS = {
  widgets: "#00bcd4",
  dashboard: "#26a69a",
  crm: "#5c6bc0",
  ai: "#7e57c2",
  development: "#ff8a65",
  resources: "#ec407a",
  tracking: "#42a5f5",
  manufacturing: "#ab47bc",
  administration: "#78909c",
  finance: "#66bb6a",
  conocimiento: "#ffa726",
  tools: "#29b6f6",
  user: "#8d6e63",
};

/** Acentos por id concreto. */
export const DASH_ITEM_ACCENTS = {
  unibox: "#0288d1",
  clients: "#3949ab",
  "kanban-clients": "#5c6bc0",
  "kanban-projects": "#7986cb",
  tareas: "#43a047",
  calendar: "#fb8c00",
  general: "#00897b",
  "design-dashboard": "#26a69a",
  "analitica-pyg": "#7e57c2",
  "analitica-marca": "#9575cd",
  pyg: "#66bb6a",
  cashflow: "#26c6da",
  insights: "#5c6bc0",
  "control-horario": "#ef6c00",
  "widget-my-tasks": "#43a047",
  "widget-novedades": "#0288d1",
  "widget-rfq-quotes": "#00897b",
  "widget-critical-path": "#8e24aa",
  "cp-gantt": "#8e24aa",
  "cp-global": "#ab47bc",
  news: "#0288d1",
  ranking: "#e53935",
  "ranking-ventas": "#e53935",
  organigrama: "#546e7a",
  "ai-search": "#7e57c2",
  "presentaciones-ai": "#9575cd",
  designs: "#ff7043",
  samples: "#ff8a65",
  lookbook: "#ffab91",
  estimates: "#ffa726",
  nominas: "#43a047",
  expenses: "#ef5350",
  "expenses-user": "#ec407a",
};

/** En dark mode subimos un poco el brillo del acento para contraste. */
export function getDashAccent(catalogId, section, mode = "light") {
  let accent = "#00bcd4";
  if (catalogId && DASH_ITEM_ACCENTS[catalogId]) {
    accent = DASH_ITEM_ACCENTS[catalogId];
  } else if (section && DASH_SECTION_ACCENTS[section]) {
    accent = DASH_SECTION_ACCENTS[section];
  }
  if (mode === "dark") {
    return lightenHex(accent, 0.18);
  }
  return accent;
}

function lightenHex(hex, amount) {
  try {
    const raw = hex.replace("#", "");
    const num = parseInt(raw.length === 3 ? raw.replace(/(.)/g, "$1$1") : raw, 16);
    const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * amount));
    const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
    const b = Math.min(255, (num & 0xff) + Math.round(255 * amount));
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  } catch {
    return hex;
  }
}

export const dashIconWellSx = (theme, accent = "#00bcd4") => {
  const isDark = theme.palette.mode === "dark";
  return {
    width: 56,
    height: 56,
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: accent,
    background: isDark
      ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(accent, 0.28)} 55%, ${alpha(accent, 0.4)} 100%)`
      : `linear-gradient(145deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha(accent, 0.16)} 48%, ${alpha(accent, 0.28)} 100%)`,
    border: `1px solid ${alpha(accent, isDark ? 0.4 : 0.28)}`,
    boxShadow: isDark
      ? `0 8px 22px ${alpha(accent, 0.28)}, inset 0 1px 0 ${alpha("#fff", 0.1)}`
      : `0 8px 20px ${alpha(accent, 0.22)}, inset 0 1px 0 ${alpha("#fff", 0.9)}, inset 0 -1px 0 ${alpha(accent, 0.1)}`,
  };
};

export const dashCountChipSx = (theme, accent) => {
  const isDark = theme.palette.mode === "dark";
  const color = accent || theme.palette.primary.main;
  return {
    ml: "auto",
    minWidth: 30,
    px: 1.1,
    py: 0.4,
    borderRadius: 999,
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: 0.2,
    textAlign: "center",
    color,
    background: isDark
      ? alpha(accent || theme.palette.primary.main, 0.18)
      : alpha("#fff", 0.75),
    border: `1px solid ${alpha(color, isDark ? 0.35 : 0.22)}`,
    boxShadow: `0 4px 12px ${alpha(color, isDark ? 0.2 : 0.14)}`,
    backdropFilter: "blur(8px)",
  };
};

export const dashListRowSx = (theme) => ({
  py: 0.7,
  px: 1.1,
  mx: -0.25,
  borderRadius: 2,
  transition: "background-color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.12 : 0.07,
    ),
    transform: "translateX(2px)",
  },
});

export const dashLinkBtnSx = (theme) => ({
  mt: 1.25,
  alignSelf: "flex-start",
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 999,
  px: 1.5,
  py: 0.4,
  color: theme.palette.primary.main,
  backgroundColor: alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.16 : 0.08,
  ),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.24 : 0.14,
    ),
  },
});

export const dashEmptySx = (theme) => ({
  mb: 2,
  p: 3,
  borderRadius: 3,
  textAlign: "center",
  border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
});

export const dashCatalogPaperSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    width: 320,
    top: 0,
    height: "100%",
    borderLeft: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.7),
    background: isDark
      ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.paper} 28%)`
      : `linear-gradient(180deg, ${alpha("#f8fcfd", 1)} 0%, ${theme.palette.background.paper} 30%)`,
    backdropFilter: "blur(12px)",
    boxShadow: isDark
      ? `-8px 0 32px ${alpha("#000", 0.35)}`
      : `-8px 0 32px ${alpha("#0f172a", 0.06)}`,
  };
};
